import React, { useMemo, useState } from "react";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { MASS_UNITS } from "../../constants";

const MAX_MIXES_DISPLAY = 999; // UI guardrail

const clampInt = (value, min = 0, max = Number.MAX_SAFE_INTEGER) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return 0;
  const i = Math.trunc(n);
  return Math.min(max, Math.max(min, i));
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const ProductionLineTable = ({
  documentData,
  setDocumentData,
  productionLists,
  moveProductionList,
  removeProductionList,
  getItemByProductRef,
  getLineByProductRef,
  handleQuantityChange,
  handleUnitChange,
  handleReworkChange,
  handleExtraChange,
  handleEnterKey,
  handleStartTimeChange, // kept for compatibility
  handleOnHandChange,
  productionDate,
  setProductionDate,
  isRowCollapsed,
  toggleRowCollapse,
}) => {
  const selected = documentData?.selectedProductionLists || [];

  // Collapsed rows "drawer" state (local UI only)
  const [collapsedDrawerOpen, setCollapsedDrawerOpen] = useState(true);

  const combinedRows = useMemo(() => {
    const rows = [];

    selected.forEach((listId) => {
      const list = productionLists.find((p) => p.id === listId);
      if (!list) return;

      let table = list.linesAndProductsTable;
      if (!table) {
        const flattened =
          list.linesAndProductsFlattened ?? list.linesAndProducts ?? [];
        if (
          Array.isArray(flattened) &&
          flattened.length > 0 &&
          flattened[0].lineId
        ) {
          table = flattened.map((p, i) => {
            const lineObj = getLineByProductRef(p.lineId);
            const itemObj = getItemByProductRef(p.productId);
            return {
              seq: i + 1,
              lineId: p.lineId,
              lineName: lineObj?.name ?? p.lineId,
              productId: p.productId,
              productName: itemObj?.name ?? p.productId,
            };
          });
        } else if (Array.isArray(list.linesAndProducts)) {
          table = [];
          list.linesAndProducts.forEach((entry) => {
            const lineRef = Object.keys(entry)[0];
            const prods = Array.isArray(entry[lineRef]) ? entry[lineRef] : [];
            prods.forEach((prodId) => {
              const lineObj = getLineByProductRef(lineRef);
              const itemObj = getItemByProductRef(prodId);
              table.push({
                seq: table.length + 1,
                lineId: lineRef,
                lineName: lineObj?.name ?? lineRef,
                productId: prodId,
                productName: itemObj?.name ?? prodId,
              });
            });
          });
        } else {
          table = [];
        }
      }

      table.forEach((r) => {
        rows.push({
          listId,
          listName: list.name,
          ...r,
        });
      });
    });

    return rows;
  }, [selected, productionLists, getLineByProductRef, getItemByProductRef]);

  const { expandedRows, collapsedRows } = useMemo(() => {
    const expanded = [];
    const collapsed = [];

    combinedRows.forEach((row) => {
      const isCollapsed = !!isRowCollapsed(row.listId, row.lineId, row.productId);
      if (isCollapsed) collapsed.push(row);
      else expanded.push(row);
    });

    return { expandedRows: expanded, collapsedRows: collapsed };
  }, [combinedRows, isRowCollapsed]);

  // Lines start-times card depends on the whole selection, not display order.
  const uniqueLines = useMemo(() => {
    const map = new Map();

    combinedRows.forEach((r) => {
      const lid = String(r.lineId);
      if (!map.has(lid)) {
        map.set(lid, {
          lineId: lid,
          lineName: r.lineName,
          listIds: new Set(),
          listNames: new Set(),
        });
      }
      const entry = map.get(lid);
      entry.listIds.add(r.listId);
      entry.listNames.add(r.listName);
    });

    const result = Array.from(map.values()).map((e) => {
      let start = "";
      for (const listId of e.listIds) {
        const candidate = documentData.startTimes?.[listId]?.[e.lineId];
        if (candidate) {
          start = candidate;
          break;
        }
        // try to match by suffix
        const suffix = String(e.lineId).split("/").pop();
        const foundKey = Object.keys(documentData.startTimes?.[listId] ?? {}).find(
          (k) => String(k).split("/").pop() === suffix
        );
        if (foundKey) {
          start = documentData.startTimes?.[listId]?.[foundKey] ?? start;
          if (start) break;
        }
      }

      return {
        lineId: e.lineId,
        lineName: e.lineName,
        listIds: Array.from(e.listIds),
        listNames: Array.from(e.listNames).join(", "),
        startTime: start ?? "",
      };
    });

    return result;
  }, [combinedRows, documentData.startTimes]);

  const getMeta = (listId, lineId, productId) => {
    const lpq = documentData?.lineProductQuantities ?? {};
    const listBlock = lpq[listId] ?? {};
    const direct = listBlock[lineId];

    if (direct && direct[productId]) return direct[productId];

    const plainLine = String(lineId).split("/").pop();
    const altLineKey = Object.keys(listBlock).find(
      (k) => String(k).split("/").pop() === plainLine
    );
    if (
      altLineKey &&
      listBlock[altLineKey] &&
      listBlock[altLineKey][productId]
    ) {
      return listBlock[altLineKey][productId];
    }

    if (direct) {
      const altProductKey = Object.keys(direct).find(
        (k) => String(k).split("/").pop() === String(productId).split("/").pop()
      );
      if (altProductKey && direct[altProductKey]) return direct[altProductKey];
    }

    return {
      quantity: "",
      onHand: "",
      extra: "",
      unit: undefined,
      rework: false,
      sequence: null,
    };
  };

  const toggleListSelection = (listId) => {
    setDocumentData((prev) => {
      const already = prev.selectedProductionLists.includes(listId);
      const updated = already
        ? prev.selectedProductionLists.filter((id) => id !== listId)
        : [...prev.selectedProductionLists, listId];
      return { ...prev, selectedProductionLists: updated };
    });
  };

  const clearSelection = () =>
    setDocumentData((prev) => ({
      ...prev,
      selectedProductionLists: [],
      lineProductQuantities: {},
      startTimes: {},
    }));

  // When update start time for a line, propagate to all selected lists that include it
  const updateStartTimeForLine = (lineId, timeValue) => {
    setDocumentData((prev) => {
      const copy = { ...prev };
      if (!copy.startTimes) copy.startTimes = {};
      const selected = copy.selectedProductionLists || [];
      selected.forEach((listId) => {
        if (!copy.startTimes[listId]) copy.startTimes[listId] = {};
        copy.startTimes[listId][lineId] = timeValue;
      });
      return copy;
    });

    if (typeof handleStartTimeChange === "function") {
      const firstList = documentData.selectedProductionLists?.[0] ?? null;
      if (firstList) handleStartTimeChange(firstList, lineId, timeValue);
    }
  };

  const computeMixInfoForRow = (row) => {
    const meta = getMeta(row.listId, row.lineId, row.productId);
    const product = getItemByProductRef(row.productId) || {};
    const ingredients = product.ingredients || [];

    if (!Array.isArray(ingredients) || ingredients.length === 0) return "";

    const doughEntry = ingredients.find((ing) =>
      Object.keys(ing)[0].startsWith("doughs/")
    );
    if (!doughEntry) return "";

    const doughRef = Object.keys(doughEntry)[0];
    const ingredientAmount = safeNumber(Object.values(doughEntry)[0], 0);

    const quantityNeeded = Math.max(
      0,
      clampInt(meta.quantity) - clampInt(meta.onHand) + clampInt(meta.extra)
    );
    const pieceCount = safeNumber(meta.unit?.pieceCount, 1) || 1;

    const lineObj = getLineByProductRef(row.lineId) || {};
    const scrapPercent =
      safeNumber(
        lineObj.products?.find((p) => Object.keys(p)[0] === row.productId)?.[
          row.productId
        ]?.scrapPercent,
        0
      ) || 0;

    const totalWithScrap =
      ingredientAmount * quantityNeeded * pieceCount * (1 + scrapPercent / 100);

    const doughObj = getItemByProductRef(doughRef) || {};
    const mixMax = safeNumber(doughObj.maxMix, 0);

    if (!Number.isFinite(mixMax) || mixMax <= 0) return "";

    let mixes = totalWithScrap / mixMax;
    if (!Number.isFinite(mixes)) return "";
    mixes = Math.min(MAX_MIXES_DISPLAY, Math.round(mixes * 100) / 100);

    const mixMaxDisplayAmount =
      Math.round((mixMax / MASS_UNITS[0].amount) * 100) / 100;

    const mixesStr = mixes % 1 === 0 ? String(mixes) : mixes.toFixed(2);
    const mixMaxStr =
      mixMaxDisplayAmount % 1 === 0
        ? String(mixMaxDisplayAmount)
        : mixMaxDisplayAmount.toFixed(2);

    return `${mixesStr} Mixes @ ${mixMaxStr}${MASS_UNITS[0].abbreviation}`;
  };

  const renderExpandedRow = (row, i) => {
    const meta = getMeta(row.listId, row.lineId, row.productId);

    const productObj = getItemByProductRef(row.productId) || {};
    const productUnits = productObj.productUnits ?? [];
    const lineObj = getLineByProductRef(row.lineId) || {};
    const mixesText = computeMixInfoForRow(row);

    return (
      <tr key={`${row.listId}-${row.lineId}-${row.productId}-${i}`}>
        <td className="smallCell">
          <span
            className="ckDisclosure"
            onClick={() => toggleRowCollapse(row.listId, row.lineId, row.productId)}
            title="Collapse row"
          >
            <span className="chev">▶</span>
          </span>
        </td>

        <td className="smallCell" style={{ color: "rgba(0,0,0,0.7)" }}>
          {i + 1}
        </td>

        <td className="ellipsis" title={row.listName}>
          {row.listName}
        </td>

        <td className="ellipsis" title={lineObj?.name ?? row.lineName}>
          {lineObj?.name ?? row.lineName}
        </td>

        <td className="ellipsis" title={row.productName}>
          {row.productName}
        </td>

        <td className="numCell">
          <input
            className="ckInput"
            type="number"
            min={0}
            value={meta.onHand ?? ""}
            onChange={(e) =>
              handleOnHandChange(row.listId, row.lineId, row.productId, e.target.value)
            }
            onKeyDown={handleEnterKey}
            style={{ width: 92 }}
          />
        </td>

        <td className="numCell">
          <input
            className="ckInput"
            type="number"
            min={0}
            value={meta.quantity ?? ""}
            onChange={(e) =>
              handleQuantityChange(row.listId, row.lineId, row.productId, e.target.value)
            }
            onKeyDown={handleEnterKey}
            style={{ width: 92 }}
          />
        </td>

        <td className="numCell">
          <input
            className="ckInput"
            type="number"
            min={0}
            value={meta.extra ?? ""}
            onChange={(e) =>
              handleExtraChange(row.listId, row.lineId, row.productId, e.target.value)
            }
            onKeyDown={handleEnterKey}
            style={{ width: 92 }}
          />
        </td>

        <td>
          <select
            className="ckSelect"
            value={meta.unit?.pieceCount ?? (productUnits[0]?.pieceCount ?? 1)}
            onChange={(e) =>
              handleUnitChange(
                row.listId,
                row.lineId,
                row.productId,
                e.target.options[e.target.selectedIndex]
              )
            }
            onKeyDown={handleEnterKey}
          >
            {productUnits && productUnits.length > 0 ? (
              <>
                {productUnits.map((u, idx) => (
                  <option key={idx} name={u.name} value={u.pieceCount}>
                    {u.name}
                  </option>
                ))}
                <option value={1} name="piece">
                  Pieces
                </option>
              </>
            ) : (
              <option value={1} name="piece">
                Pieces
              </option>
            )}
          </select>
        </td>

        <td className="smallCell">
          <input
            type="checkbox"
            checked={!!meta.rework}
            onChange={(e) =>
              handleReworkChange(row.listId, row.lineId, row.productId, e.target.checked)
            }
          />
        </td>

        <td className="mixCell" title={mixesText}>
          {mixesText}
        </td>
      </tr>
    );
  };

  const renderCollapsedRow = (row, i) => {
    const lineObj = getLineByProductRef(row.lineId) || {};
    return (
      <tr key={`collapsed-${row.listId}-${row.lineId}-${row.productId}-${i}`}>
        <td colSpan={11} style={{ paddingTop: 6, paddingBottom: 6 }}>
          <span
            className="ckDisclosure"
            onClick={() => toggleRowCollapse(row.listId, row.lineId, row.productId)}
            title="Show row"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <span className="chev" style={{ minWidth: 16 }}>
              ▼
            </span>
            <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span>
                <strong>{row.productName}</strong>{" "}
                <span style={{ fontWeight: 400, color: "rgba(0,0,0,0.65)" }}>
                  (collapsed — click to expand)
                </span>
              </span>
              <span style={{ color: "rgba(0,0,0,0.55)" }}>• {row.listName}</span>
              <span style={{ color: "rgba(0,0,0,0.55)" }}>
                • {lineObj?.name ?? row.lineName}
              </span>
            </span>
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="productionPage">
      <div className="ckCard" style={{ marginBottom: 12 }}>
        <div className="ckRow" style={{ justifyContent: "space-between" }}>
          <div>
            <div className="ckFieldLabel">Production date</div>
            <input
              className="ckInput"
              type="date"
              value={productionDate}
              onChange={(e) => setProductionDate(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <button className="ckButton" onClick={clearSelection}>
              Clear selection
            </button>
          </div>
        </div>
      </div>

      <div className="ckCard" style={{ marginBottom: 12 }}>
        <div className="ckFieldLabel">Production lists</div>
        <div
          style={{
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: 12,
            padding: 10,
            background: "#fff",
          }}
        >
          {productionLists.map((list) => (
            <div
              key={list.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 2px",
              }}
            >
              <input
                type="checkbox"
                id={`prod-list-${list.id}`}
                checked={documentData.selectedProductionLists.includes(list.id)}
                onChange={() => toggleListSelection(list.id)}
              />
              <label
                htmlFor={`prod-list-${list.id}`}
                style={{ cursor: "pointer" }}
              >
                {list.name}
              </label>
            </div>
          ))}
        </div>

        {selected.length > 0 && (
          <>
            <div className="ckFieldLabel" style={{ marginTop: 12 }}>
              Selected (drag order with arrows)
            </div>
            <div className="ckRow" style={{ gap: 8 }}>
              {selected.map((listId, idx) => {
                const list = productionLists.find((p) => p.id === listId);
                if (!list) return null;
                return (
                  <div key={listId} className="ckPill">
                    <strong>{list.name}</strong>
                    {idx > 0 && (
                      <ArrowCircleUpIcon
                        onClick={() => moveProductionList(idx, -1)}
                        className="ckIconBtn"
                        titleAccess="Move up"
                      />
                    )}
                    {idx < selected.length - 1 && (
                      <ArrowCircleDownIcon
                        onClick={() => moveProductionList(idx, 1)}
                        className="ckIconBtn"
                        titleAccess="Move down"
                      />
                    )}
                    <RemoveCircleOutlineIcon
                      onClick={() => removeProductionList(listId)}
                      className="ckIconBtn"
                      titleAccess="Remove list"
                    />
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {uniqueLines.length > 0 && (
        <div className="ckCard" style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>
            Line start times
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {uniqueLines.map((entry) => (
              <div
                key={entry.lineId}
                className="ckRow"
                style={{ justifyContent: "space-between" }}
              >
                <div style={{ minWidth: 280 }}>
                  <div style={{ fontWeight: 650 }} title={entry.lineName}>
                    {entry.lineName}
                  </div>
                  <div
                    style={{ fontSize: 12, color: "rgba(0,0,0,0.60)" }}
                    title={entry.listNames}
                  >
                    {entry.listNames}
                  </div>
                </div>

                <div className="ckRow">
                  <div className="ckFieldLabel" style={{ marginBottom: 0 }}>
                    Start
                  </div>
                  <input
                    className="ckInput"
                    type="time"
                    value={entry.startTime ?? ""}
                    onChange={(e) =>
                      updateStartTimeForLine(entry.lineId, e.target.value)
                    }
                    onKeyDown={handleEnterKey}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ckTableWrap">
        <table className="ckTable">
          <thead>
            <tr>
              <th className="smallCell"></th>
              <th className="smallCell">#</th>
              <th>List</th>
              <th>Line</th>
              <th>Product</th>
              <th className="numCell">On Hand</th>
              <th className="numCell">Qty</th>
              <th className="numCell">Extra</th>
              <th>Unit</th>
              <th className="smallCell">Rework</th>
              <th>Mixes</th>
            </tr>
          </thead>

          <tbody>
            {combinedRows.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ padding: 14, color: "rgba(0,0,0,0.6)" }}>
                  No rows — select production lists to populate the table.
                </td>
              </tr>
            ) : (
              <>
                {/* Expanded rows always show first */}
                {expandedRows.map((row, i) => {
                  const prevRow = expandedRows[i - 1];
                  const isNewList = i > 0 && prevRow?.listId !== row.listId;
                  return (
                    <React.Fragment key={`expanded-group-${row.listId}-${row.lineId}-${row.productId}-${i}`}>
                      {isNewList && (
                        <tr>
                          <td
                            colSpan={11}
                            style={{
                              padding: 0,
                              height: 0,
                              borderTop: "4px solid rgba(0,0,0,0.9)",
                            }}
                          />
                        </tr>
                      )}
                      {renderExpandedRow(row, i)}
                    </React.Fragment>
                  );
                })}

                {/* Collapsed rows bucket at bottom; bucket itself can be collapsed */}
                {collapsedRows.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={11} style={{ padding: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "10px 12px",
                            background: "rgba(0,0,0,0.03)",
                            borderTop: "1px solid rgba(0,0,0,0.08)",
                          }}
                        >
                          <span
                            className="ckDisclosure"
                            onClick={() => setCollapsedDrawerOpen((v) => !v)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            title={
                              collapsedDrawerOpen
                                ? "Hide collapsed rows"
                                : "Show collapsed rows"
                            }
                          >
                            <span className="chev" style={{ minWidth: 16 }}>
                              {collapsedDrawerOpen ? "▼" : "▶"}
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              Collapsed items ({collapsedRows.length})
                            </span>
                            <span style={{ fontWeight: 400, color: "rgba(0,0,0,0.65)" }}>
                              {collapsedDrawerOpen
                                ? " (click to hide)"
                                : " (click to show)"}
                            </span>
                          </span>

                          {collapsedDrawerOpen && (
                            <button
                              className="ckButton"
                              onClick={() => setCollapsedDrawerOpen(false)}
                              style={{ padding: "6px 10px" }}
                              title="Hide collapsed rows"
                            >
                              Hide
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {collapsedDrawerOpen && (
                      <>
                        {collapsedRows.map((row, i) => {
                          const prevRow = collapsedRows[i - 1];
                          const isNewList = i > 0 && prevRow?.listId !== row.listId;
                          return (
                            <React.Fragment
                              key={`collapsed-group-${row.listId}-${row.lineId}-${row.productId}-${i}`}
                            >
                              {isNewList && (
                                <tr>
                                  <td
                                    colSpan={11}
                                    style={{
                                      padding: 0,
                                      height: 0,
                                      borderTop: "4px solid rgba(0,0,0,0.9)",
                                    }}
                                  />
                                </tr>
                              )}
                              {renderCollapsedRow(row, i)}
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductionLineTable;
